// Jobs Board — Kanban view at /admin/jobs-board. Two column-grouping
// modes: by status or by tech. Now supports:
// • Click checkbox on a card to toggle selection
// • Cmd/Ctrl-click a card to toggle selection (else regular click navigates)
// • Drag a lasso across the columns to select multiple cards at once
// • Sticky Bulk Action Bar appears when 1+ selected (Assign / Status / CSV / Cancel / Delete)

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Kanban, Users, ListChecks, User as UserIcon,
  Clock, MapPin, ArrowRight, ArrowsClockwise, Download, Trash, X, CheckCircle,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import VendorChipBar from "@/components/VendorChipBar";
import { useVendorFilter } from "@/contexts/VendorFilterContext";
import BulkAssignModal from "@/components/BulkAssignModal";
import PinToSweeperButton, { pinDateLabel } from "@/components/PinToSweeperButton";
import { ReturnBadge } from "@/components/ReturnLater";
import { TriageMenu, TRIAGE_ACTIONS, runTriage, useSwipeLeft } from "@/components/TriageMenu";

const MOVE_TO_STATUSES = [
  { key: "pool",        label: "Pool",        color: "amber" },
  { key: "assigned",    label: "Assigned",    color: "sky" },
  { key: "in_progress", label: "In Progress", color: "violet" },
  { key: "follow_up",   label: "Follow-up",   color: "orange" },
  { key: "needs_quote", label: "Needs Quote", color: "fuchsia" },
  { key: "on_hold",     label: "On Hold",     color: "slate" },
  { key: "completed",   label: "Completed",   color: "emerald" },
];

const STATUS_COLUMNS = [
  { key: "pool",        label: "Pool",        color: "amber",    hint: "Unassigned" },
  { key: "assigned",    label: "Assigned",    color: "sky",      hint: "Waiting on tech" },
  { key: "in_progress", label: "In Progress", color: "violet",   hint: "Being worked" },
  { key: "follow_up",   label: "Follow-up",   color: "orange",   hint: "Waiting on client" },
  { key: "needs_quote", label: "Needs Quote", color: "fuchsia",  hint: "Estimate required" },
  { key: "on_hold",     label: "On Hold",     color: "slate",    hint: "Parked / not now" },
  { key: "completed",   label: "Completed",   color: "emerald",  hint: "Done" },
];

const COLOR_MAP = {
  amber:   { pill: "bg-amber-100 text-amber-800 border-amber-300", bar: "bg-amber-500" },
  sky:     { pill: "bg-sky-100 text-sky-800 border-sky-300",       bar: "bg-sky-500" },
  violet:  { pill: "bg-violet-100 text-violet-800 border-violet-300", bar: "bg-violet-500" },
  orange:  { pill: "bg-orange-100 text-orange-800 border-orange-300", bar: "bg-orange-500" },
  fuchsia: { pill: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300", bar: "bg-fuchsia-500" },
  emerald: { pill: "bg-emerald-100 text-emerald-800 border-emerald-300", bar: "bg-emerald-500" },
  slate:   { pill: "bg-slate-100 text-slate-700 border-slate-300", bar: "bg-slate-400" },
};

function ageOf(iso) {
  if (!iso) return null;
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 86400000;
    if (diff < 1) return "today";
    if (diff < 2) return "1d";
    if (diff < 7) return `${Math.floor(diff)}d`;
    if (diff < 30) return `${Math.floor(diff / 7)}w`;
    return `${Math.floor(diff / 30)}mo`;
  } catch { return null; }
}

export default function AdminJobsBoard() {
  const [groupBy, setGroupBy] = useState("status");
  const [jobs, setJobs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hideCompleted, setHideCompleted] = useState(true);
  const [q, setQ] = useState("");
  const { asQuery: vendorQuery } = useVendorFilter();

  // ── Bulk-selection state ────────────────────────────────────────────────
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  // ── Drag-lasso state ────────────────────────────────────────────────────
  const boardRef = useRef(null);
  const [lasso, setLasso] = useState(null); // { startX, startY, curX, curY }
  const dragMovedRef = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const jobsUrl = "/jobs" + (vendorQuery ? `?vendors=${encodeURIComponent(vendorQuery)}` : "");
      const [j, w] = await Promise.all([
        api.get(jobsUrl),
        api.get("/assignable-users"),
      ]);
      setJobs(j.data || []);
      setWorkers(w.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [vendorQuery]);

  const filteredJobs = useMemo(() => {
    const term = q.trim().toLowerCase();
    return jobs.filter(j => {
      if (hideCompleted && (j.status === "completed" || j.status === "canceled")) return false;
      if (!term) return true;
      return (
        (j.title || "").toLowerCase().includes(term) ||
        (j.property_address || "").toLowerCase().includes(term) ||
        (j.assigned_to_name || "").toLowerCase().includes(term) ||
        (j.work_order_number || "").toLowerCase().includes(term)
      );
    });
  }, [jobs, hideCompleted, q]);

  const columns = useMemo(() => {
    if (groupBy === "status") {
      return STATUS_COLUMNS
        .filter(c => !(hideCompleted && c.key === "completed"))
        .map(c => ({
          key: c.key, label: c.label, color: c.color, hint: c.hint,
          items: filteredJobs.filter(j => (j.status || "pool") === c.key),
        }));
    }
    const byTech = new Map();
    byTech.set("__unassigned__", { key: "__unassigned__", label: "Unassigned", color: "amber", hint: "No tech yet", items: [] });
    workers.forEach(w => {
      byTech.set(w.id || w.name, {
        key: w.id || w.name, label: w.name || "(unnamed)", color: "sky",
        hint: w.role || "Tech", items: [],
      });
    });
    filteredJobs.forEach(j => {
      const tid = j.assigned_to || null;
      const tname = j.assigned_to_name || "";
      if (!tid && !tname) {
        byTech.get("__unassigned__").items.push(j);
        return;
      }
      let col = tid ? byTech.get(tid) : null;
      if (!col && tname) {
        col = [...byTech.values()].find(c => c.label === tname);
        if (!col) {
          col = { key: `name:${tname}`, label: tname, color: "sky", hint: "Tech (unlinked)", items: [] };
          byTech.set(col.key, col);
        }
      }
      (col || byTech.get("__unassigned__")).items.push(j);
    });
    return [...byTech.values()].filter(c => c.items.length > 0 || c.key === "__unassigned__");
  }, [filteredJobs, groupBy, workers, hideCompleted]);

  const totalVisible = columns.reduce((s, c) => s + c.items.length, 0);

  // ── Selection helpers ───────────────────────────────────────────────────
  const toggleId = (id) => {
    setSelected(prev => {
      const nxt = new Set(prev);
      if (nxt.has(id)) nxt.delete(id); else nxt.add(id);
      return nxt;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const selectAllVisible = () => setSelected(new Set(filteredJobs.map(j => j.id)));

  // ── Drag-lasso mouse handlers ───────────────────────────────────────────
  const onBoardMouseDown = (e) => {
    // Only start lasso when mousing down on empty space (not on a card / control)
    if (e.button !== 0) return;
    if (e.target.closest("[data-jobcard]") || e.target.closest("input,button,a,label")) return;
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragMovedRef.current = false;
    setLasso({
      startX: e.clientX - rect.left + boardRef.current.scrollLeft,
      startY: e.clientY - rect.top + boardRef.current.scrollTop,
      curX:   e.clientX - rect.left + boardRef.current.scrollLeft,
      curY:   e.clientY - rect.top + boardRef.current.scrollTop,
      additive: e.shiftKey || e.metaKey || e.ctrlKey,
    });
  };
  const onBoardMouseMove = (e) => {
    if (!lasso) return;
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nx = e.clientX - rect.left + boardRef.current.scrollLeft;
    const ny = e.clientY - rect.top + boardRef.current.scrollTop;
    if (Math.abs(nx - lasso.startX) > 3 || Math.abs(ny - lasso.startY) > 3) dragMovedRef.current = true;
    setLasso(l => l ? { ...l, curX: nx, curY: ny } : null);
  };
  const onBoardMouseUp = () => {
    if (!lasso) return;
    if (dragMovedRef.current) {
      // Compute lasso rect (viewport-relative — use client rects to match).
      const boardRect = boardRef.current?.getBoundingClientRect();
      if (!boardRect) { setLasso(null); return; }
      const x1 = Math.min(lasso.startX, lasso.curX) - boardRef.current.scrollLeft + boardRect.left;
      const y1 = Math.min(lasso.startY, lasso.curY) - boardRef.current.scrollTop + boardRect.top;
      const x2 = Math.max(lasso.startX, lasso.curX) - boardRef.current.scrollLeft + boardRect.left;
      const y2 = Math.max(lasso.startY, lasso.curY) - boardRef.current.scrollTop + boardRect.top;
      const cardEls = boardRef.current.querySelectorAll("[data-jobcard]");
      const hits = new Set();
      cardEls.forEach(el => {
        const r = el.getBoundingClientRect();
        const overlap = !(r.right < x1 || r.left > x2 || r.bottom < y1 || r.top > y2);
        if (overlap) hits.add(el.getAttribute("data-jobcard"));
      });
      setSelected(prev => {
        if (lasso.additive) {
          const nxt = new Set(prev);
          hits.forEach(id => nxt.add(id));
          return nxt;
        }
        return hits;
      });
      if (hits.size) toast.success(`${hits.size} selected`);
    }
    setLasso(null);
    dragMovedRef.current = false;
  };

  // ── Bulk actions (mirror the /admin/jobs list-view actions) ────────────
  const bulkStatus = async (status) => {
    if (selected.size === 0 || !status) return;
    setBulkBusy(true);
    const t = toast.loading(`Moving ${selected.size} jobs → ${status.replace("_", " ")}…`);
    try {
      const r = await api.post("/jobs/bulk-status", {
        job_ids: Array.from(selected), status,
      });
      toast.success(`${r.data.updated} moved · ${r.data.skipped} skipped`, { id: t });
      clearSelection();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Bulk status failed"), { id: t });
    } finally { setBulkBusy(false); }
  };
  const bulkExportCsv = () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected).join(",");
    const url = `${api.defaults.baseURL}/jobs/bulk-export.csv?ids=${encodeURIComponent(ids)}`;
    const tok = localStorage.getItem("token") || "";
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `field-radar-jobs-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        toast.success(`Exported ${selected.size} jobs to CSV`);
      })
      .catch(() => toast.error("CSV export failed"));
  };
  const bulkCancel = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Cancel ${selected.size} jobs?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/jobs/bulk-status", { job_ids: Array.from(selected), status: "canceled" });
      toast.success(`${r.data.updated} canceled · ${r.data.skipped} skipped`);
      clearSelection(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Cancel failed")); }
    finally { setBulkBusy(false); }
  };

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-full mx-auto" data-testid="jobs-board-page">
        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-violet-900 to-slate-950 text-white p-6 sm:p-8 mb-5">
          <div className="flex items-center gap-3 mb-1">
            <Kanban weight="fill" size={28} />
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-none tracking-tight">
              Jobs Board
            </h1>
          </div>
          <p className="mt-2 text-sm text-white/80 max-w-xl">
            Visual dispatch view. Tap a card to open it. <b>Click the ☐</b> or <b>drag a box</b> across cards to bulk-select — then assign, status-change, or export in one hit.
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="jobs-board-controls">
          <div className="inline-flex bg-slate-100 border-2 border-slate-200 p-0.5" data-testid="jobs-board-groupby">
            <button onClick={() => setGroupBy("status")}
              data-testid="group-status"
              className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${groupBy === "status" ? "bg-white text-slate-900 border border-slate-300" : "text-slate-600 hover:text-slate-900"}`}>
              <ListChecks size={13}/> By Status
            </button>
            <button onClick={() => setGroupBy("tech")}
              data-testid="group-tech"
              className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${groupBy === "tech" ? "bg-white text-slate-900 border border-slate-300" : "text-slate-600 hover:text-slate-900"}`}>
              <Users size={13}/> By Tech
            </button>
          </div>

          <label className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-700">
            <input type="checkbox" checked={hideCompleted}
              onChange={e => setHideCompleted(e.target.checked)}
              data-testid="hide-completed"/>
            Hide completed
          </label>

          <input list="known-addresses" autoComplete="off" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search title / address / tech / WO#"
            data-testid="jobs-board-search"
            className="h-9 px-3 border-2 border-slate-300 text-xs font-medium flex-1 min-w-[220px] max-w-md"/>

          <button onClick={selectAllVisible} data-testid="board-select-all"
            className="h-9 px-3 border-2 border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-black uppercase text-[10px] tracking-widest rounded">
            Select All Visible
          </button>

          <div className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500"
            data-testid="jobs-board-count">
            {totalVisible} of {jobs.length}
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selected.size > 0 && (
          <div className="sticky top-2 z-30 mb-3 bg-white border-2 border-emerald-500 rounded-lg shadow-[6px_6px_0_#10b981] p-2 flex flex-wrap items-center gap-2"
            data-testid="kanban-bulk-action-bar">
            <div className="flex items-center gap-2 px-2">
              <CheckCircle weight="fill" size={16} className="text-emerald-600"/>
              <div className="font-display font-black uppercase tracking-widest text-xs text-emerald-900">
                {selected.size} selected
              </div>
            </div>
            <button onClick={clearSelection} data-testid="kanban-bulk-clear"
              className="h-9 px-3 border-2 border-slate-900 text-slate-900 font-black uppercase text-xs tracking-wider hover:bg-slate-900 hover:text-white">
              Clear
            </button>
            <button onClick={() => setBulkAssignOpen(true)} disabled={bulkBusy}
              data-testid="kanban-bulk-assign-btn"
              className="h-9 px-3 bg-cyan-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-cyan-700 disabled:opacity-60">
              <Users weight="bold" size={14}/> Assign
            </button>
            <button onClick={bulkExportCsv} disabled={bulkBusy}
              data-testid="kanban-bulk-csv-btn"
              className="h-9 px-3 bg-amber-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-amber-700 disabled:opacity-60">
              <Download weight="bold" size={14}/> CSV
            </button>
            <PinToSweeperButton jobIds={Array.from(selected)} workers={workers} disabled={bulkBusy}
              onPinned={() => { clearSelection(); load(); }}/>
            <button onClick={bulkCancel} disabled={bulkBusy}
              data-testid="kanban-bulk-cancel-btn"
              className="h-9 px-3 bg-red-500 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-red-600 disabled:opacity-60">
              <X weight="bold" size={14}/> Cancel
            </button>
            <div className="w-full flex flex-wrap items-center gap-1.5 px-2 pt-1 border-t border-emerald-100" data-testid="kanban-bulk-status-chips">
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 mr-1 inline-flex items-center gap-1"><ArrowsClockwise size={11}/> Move to</span>
              {MOVE_TO_STATUSES.map(s => (
                <button key={s.key} onClick={() => bulkStatus(s.key)} disabled={bulkBusy}
                  data-testid={`bulk-move-${s.key}`}
                  className={`h-7 px-2.5 border text-[10px] font-black uppercase tracking-wider rounded-full hover:brightness-95 disabled:opacity-60 ${COLOR_MAP[s.color]?.pill || COLOR_MAP.slate.pill}`}>
                  {s.label}
                </button>
              ))}
              <span className="mx-1 text-slate-300">|</span>
              {TRIAGE_ACTIONS.filter(a => !a.sweeperOnly).map(a => (
                <button key={a.key} disabled={bulkBusy} data-testid={`bulk-triage-${a.key}`}
                  onClick={async () => {
                    try { const r = await runTriage(Array.from(selected), a, { askNote: a.note }); if (r) { clearSelection(); load(); } }
                    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Triage failed")); }
                  }}
                  className={`h-7 px-2.5 border border-slate-300 bg-white text-[10px] font-black uppercase tracking-wider rounded-full inline-flex items-center gap-1 ${a.cls}`}>
                  <a.icon weight="fill" size={11}/> {a.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <VendorChipBar className="mt-3"/>

        {loading && <div className="mt-8 text-center text-slate-500 font-bold uppercase text-xs">Loading…</div>}

        {!loading && (
          <div
            ref={boardRef}
            onMouseDown={onBoardMouseDown}
            onMouseMove={onBoardMouseMove}
            onMouseUp={onBoardMouseUp}
            onMouseLeave={() => { if (lasso) { setLasso(null); dragMovedRef.current = false; } }}
            className="relative flex gap-3 overflow-x-auto pb-4 -mx-4 sm:-mx-6 px-4 sm:px-6 select-none"
            data-testid="jobs-board-columns"
          >
            {columns.map(col => (
              <div key={col.key} data-testid={`jobs-board-col-${col.key}`}
                className="flex-shrink-0 w-72 sm:w-80 bg-slate-50 border-2 border-slate-200 rounded-lg overflow-hidden">
                <div className={`h-1.5 ${COLOR_MAP[col.color]?.bar || "bg-slate-400"}`}/>
                <div className="p-3 bg-white border-b-2 border-slate-100 flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-black uppercase text-[11px] tracking-widest text-slate-800 truncate">{col.label}</div>
                    <div className="text-[10px] text-slate-500 truncate">{col.hint}</div>
                  </div>
                  <div className={`inline-flex items-center px-2 py-0.5 border font-black text-[11px] ${COLOR_MAP[col.color]?.pill || "bg-slate-100 text-slate-700 border-slate-300"}`}
                    data-testid={`col-count-${col.key}`}>
                    {col.items.length}
                  </div>
                </div>
                <div className="p-2 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto min-h-[80px]">
                  {col.items.length === 0 && (
                    <div className="text-center text-[11px] text-slate-400 italic py-4">Empty</div>
                  )}
                  {col.items.map(j => (
                    <JobCard key={j.id} job={j} groupBy={groupBy}
                      selected={selected.has(j.id)} onToggle={() => toggleId(j.id)}
                      onTriaged={() => setJobs(prev => prev.filter(x => x.id !== j.id))}/>
                  ))}
                </div>
              </div>
            ))}

            {/* Lasso overlay */}
            {lasso && dragMovedRef.current && (
              <div className="absolute pointer-events-none border-2 border-emerald-500 bg-emerald-500/10 rounded-sm z-40"
                style={{
                  left: Math.min(lasso.startX, lasso.curX),
                  top:  Math.min(lasso.startY, lasso.curY),
                  width:  Math.abs(lasso.curX - lasso.startX),
                  height: Math.abs(lasso.curY - lasso.startY),
                }}
              />
            )}
          </div>
        )}

        {bulkAssignOpen && (
          <BulkAssignModal
            jobIds={Array.from(selected)}
            onClose={() => setBulkAssignOpen(false)}
            onDone={() => { clearSelection(); load(); }}
          />
        )}
      </div>
    </AdminShell>
  );
}

function JobCard({ job, groupBy, selected, onToggle, onTriaged }) {
  const age = ageOf(job.created_at);
  const nav = useNavigate();
  const [swipeMenu, setSwipeMenu] = useState(false);
  const swipe = useSwipeLeft(() => setSwipeMenu(true));
  const onCardClick = (e) => {
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      onToggle();
      return;
    }
    // Regular click → navigate
    nav(`/admin/jobs/${job.id}`);
  };
  return (
    <div
      {...swipe}
      data-jobcard={job.id}
      data-testid={`jobs-board-card-${job.id}`}
      onClick={onCardClick}
      className={`block relative cursor-pointer bg-white border-2 hover:border-slate-400 hover:shadow-md transition p-2.5 rounded group ${selected ? "border-emerald-500 shadow-[3px_3px_0_#10b981] bg-emerald-50/40" : "border-slate-200"}`}
    >
      {/* Selection checkbox — always visible so clicking the ☐ is obvious */}
      <label className="absolute top-1.5 left-1.5 z-10 inline-flex" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={!!selected} onChange={onToggle}
          data-testid={`jobcard-select-${job.id}`}
          className="w-4 h-4 accent-emerald-600 cursor-pointer"/>
      </label>
      <div className="absolute top-1 right-1 z-10">
        <TriageMenu jobId={job.id} onDone={onTriaged}/>
        {swipeMenu && <TriageMenu jobId={job.id} open onClose={() => setSwipeMenu(false)} onDone={onTriaged}/>}
      </div>
      <div className="flex items-start gap-2 pl-6 pr-5">
        <div className="flex-1 min-w-0">
          <div className="font-black text-[13px] text-slate-900 leading-tight line-clamp-2">
            {job.title || "(untitled)"}
          </div>
          {job.property_address && (
            <div className="mt-1 text-[11px] text-slate-600 inline-flex items-center gap-1 line-clamp-1">
              <MapPin size={10} className="flex-shrink-0"/>
              <span className="truncate">
                {job.property_address}{job.unit ? ` · ${job.unit}` : ""}
              </span>
            </div>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {job.portfolio_name && (
              <span className="text-[9px] font-black uppercase tracking-widest text-white px-1.5 py-0.5 rounded"
                style={{ background: job.portfolio_color || "#0f172a" }} data-testid={`jobcard-portfolio-${job.id}`}>
                {job.portfolio_name}
              </span>
            )}
            <ReturnBadge rv={job.return_visit} testId={`jobcard-return-${job.id}`}/>
            {job.sweeper_pin && (
              <span className="text-[9px] font-black uppercase tracking-widest bg-amber-400 text-slate-900 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"
                title={`Pinned to ${job.sweeper_pin.truck_label} for ${job.sweeper_pin.date}`} data-testid={`jobcard-pin-${job.id}`}>
                📌 {job.sweeper_pin.truck_label} · {pinDateLabel(job.sweeper_pin.date)}
              </span>
            )}
            {job.work_order_number && (
              <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1 py-0.5 rounded">
                WO#{job.work_order_number}
              </span>
            )}
            {groupBy === "status" && job.assigned_to_name && (
              <span className="text-[10px] text-slate-600 inline-flex items-center gap-0.5">
                <UserIcon size={9}/> {job.assigned_to_name}
              </span>
            )}
            {age && (
              <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                <Clock size={9}/> {age}
              </span>
            )}
          </div>
        </div>
        <ArrowRight size={14} className="text-slate-300 group-hover:text-slate-600 mt-1 flex-shrink-0"/>
      </div>
    </div>
  );
}
